/**
 * Singleton, CSS Bundler
 * R&D: Alexander.Bondar@ingrammicro.com
 * Jira: https://jira.int.zone/browse/OA-20116 https://jira.int.zone/browse/PSA-1252
 **/

import 'construct-style-sheets-polyfill';

import { State } from 'vuex-simple';
import { spriteStyleUrl, defaultStyleSheetsURLs, spriteSheetUrl } from '@/Common/Environment/const';

import BaseBundlerModule from '@/HostApp/services/CSSBundler/BaseBundlerModule';

export class CSSBundlerModule extends BaseBundlerModule {
	private readonly storage: Map<string, Promise<CSSStyleSheet>>;
	private readonly cssMixins: Array<Promise<CSSStyleSheet>>;
	private readonly cssVariablesMixin: CSSStyleSheet;
	private readonly iframeVariablesMixins: Array<CSSStyleSheet>;
	private readonly isCSSStyleSheetNativeSupport: boolean;

	constructor(mode: RequestMode = 'cors') {
		super(mode);
		this.storage = new Map();
		this.isCSSStyleSheetNativeSupport = StyleSheet.isPrototypeOf(CSSStyleSheet);

		this.iframeVariablesMixins = [];

		const initialVariablesValues = {} as Common.IMap<string>;
		this.cssVariablesMixin = this.createCSSVariablesMixin(initialVariablesValues);

		const [spritesCSSFixMixin] = this.createSpritesCssFixMixin();
		this.cssMixins = [Promise.resolve(this.cssVariablesMixin), spritesCSSFixMixin];
	}

	@State()
	public loading: boolean = false;

	@State()
	public api: Partial<CSSBundlerModule> = {
		applyCSSBundles: (el: Document | ShadowRoot | null, urls: Array<string> = []) => {
			return this.applyCSSBundles(el, urls);
		},

		applyCSSVariablesOnly: (el: Document | ShadowRoot | null) => {
			return this.applyCSSVariablesOnly(el);
		},

		applyCSSBundlesToIframe: (iframe, urls, variables): Promise<void> => {
			return this.applyCSSBundlesToIframe(iframe, urls, variables);
		}
	};

	private createCSSVariablesMixin(initialValues: Common.IMap<string>): CSSStyleSheet {
		return new CSSStyleSheet();
	}

	private readonly patchAndCreateSpriteCss = (data: Promise<string>): Promise<CSSStyleSheet> => {
		return data.then((styles) => {
			const fixedStyles = styles.replaceAll('svg/sprite.view.svg', spriteSheetUrl);
			return this.createCSSStyleSheet(Promise.resolve(fixedStyles));
		});
	};

	private createSpritesCssFixMixin(): Array<Promise<CSSStyleSheet>> {
		return this.fetchUrls([spriteStyleUrl], this.storage, this.patchAndCreateSpriteCss);
	}

	public getCSSStyleSheetByUrl(url: string): Promise<CSSStyleSheet> | undefined {
		return this.storage.get(url);
	}

	public prefetchStyleSheets(urls: Array<string> = []): void {
		const styleSheetsUrls = [...defaultStyleSheetsURLs, ...urls];
		this.fetchUrls(styleSheetsUrls, this.storage, this.createCSSStyleSheet);
	}

	private fetchStyleSheets(urls: Array<string> = []): Array<Promise<CSSStyleSheet>> {
		return this.fetchUrls(urls, this.storage, this.createCSSStyleSheet);
	}

	protected createCSSStyleSheet(data: Promise<string>): Promise<CSSStyleSheet> {
		return data.then((styles) => {
			const cssStyleSheet = new CSSStyleSheet();
			cssStyleSheet.replaceSync(styles);

			return cssStyleSheet;
		});
	}

	public setCSSVariableValue(varName: string, value: string): void {
		const mixins = [this.cssVariablesMixin, ...this.iframeVariablesMixins];

		mixins.forEach((mixin) => {
			const { cssRules } = mixin;

			const index = Array.from(cssRules).findIndex((item) => item.style.item(0) === varName);
			index > -1 && mixin.deleteRule(index);

			mixin.insertRule(`:root {${varName}: ${value}}`);
		});

		//fallback for cross-origin iframes
		Array.from(window.frames).forEach((frame) => {
			frame.postMessage(['SET-CSS-RULE', { varName, varValue: value }], '*');
		});
	}

	public applyCSSBundlesToIframe(
		iframe: HTMLIFrameElement,
		urls: Array<string> = [],
		variables: Common.IMap<string>
	): Promise<void> {
		const contentWindow = iframe.contentWindow;

		if (contentWindow && iframe.contentDocument) {
			this.loading = true;
			const contentDocument = iframe.contentDocument || contentWindow?.document;
			const resolvedVariablesMixin = Promise.resolve(this.cssVariablesMixin);
			const cssStyleSheets = [resolvedVariablesMixin, ...this.fetchStyleSheets(urls)];

			return Promise.all(cssStyleSheets)
				.then((resolvedStyleSheets) => {
					const newCSSStyleSheets = resolvedStyleSheets.map((styleSheet) => {
						const cssStyleSheet = this.createCSSStyleSheetForIFrame(contentWindow, contentDocument);

						return Array.from(styleSheet.cssRules).reduce((acc, cssRule, idx) => {
							acc.insertRule(cssRule.cssText, idx);
							return acc;
						}, cssStyleSheet);
					});

					if (this.isCSSStyleSheetNativeSupport) {
						contentDocument.adoptedStyleSheets = newCSSStyleSheets;
					}

					const [variablesMixin] = newCSSStyleSheets;
					this.iframeVariablesMixins.push(variablesMixin);
				})

				.catch((e) => {
					// eslint-disable-next-line no-console
					console.warn('applyCSSBundlesToIframe: something went wrong during css injecting:', e);
				})
				.finally(() => {
					this.loading = false;
				});
		} else {
			if (contentWindow) {
				// fallback for cross-origin iframes
				Object.keys(variables).forEach((varName) => {
					const varValue = variables[varName];
					contentWindow.postMessage(['SET-CSS-RULE', { varName, varValue }], '*');
				});
			}

			return Promise.resolve();
		}
	}

	public applyCSSBundles(
		el: Document | ShadowRoot | null,
		urls: Array<string> = defaultStyleSheetsURLs
	): Promise<void> {
		if (el) {
			this.loading = true;
			const cssStyleSheets = [...this.fetchStyleSheets(urls), ...this.cssMixins];

			return Promise.all(cssStyleSheets)
				.then((resolvedStyleSheets) => {
					//needs to prevent host styles under the shadow root
					resolvedStyleSheets[0].insertRule(`:host { all: initial; }`, 0);

					el.adoptedStyleSheets = resolvedStyleSheets;
				})
				.catch((e) => {
					// eslint-disable-next-line no-console
					console.warn(`applyStyleSheets: Something went wrong, can't set adoptedStyleSheets: `, e);
				})
				.finally(() => {
					this.loading = false;
				});
		}
		return Promise.resolve();
	}

	public applyCSSVariablesOnly(el: Document | ShadowRoot | null): void {
		if (el) {
			el.adoptedStyleSheets = [this.cssVariablesMixin];
		} else {
			// eslint-disable-next-line no-console
			console.warn(`applyCssVariablesOnly: Something went wrong, can't set adoptedStyleSheets`);
		}
	}

	private createCSSStyleSheetForIFrame(contentWindow: Window, contentDocument: Document): CSSStyleSheet {
		if (this.isCSSStyleSheetNativeSupport) {
			return new contentWindow.CSSStyleSheet();
		} else {
			const style = contentDocument.createElement('style');
			contentDocument.head.appendChild(style);

			return style.sheet as CSSStyleSheet;
		}
	}
}

const cssBundlerModule = new CSSBundlerModule();

export default cssBundlerModule;
